import React, { useState } from 'react';
import { Drawer, Timeline, Collapse, Typography, Spin, Alert } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useGetApiAdminSearchHistorySearchIdViews,
  useGetApiAdminGuidesGuideId,
  type GuideViewResponse,
  type ResolutionFeedback,
} from '@/features/guides/api/generated';

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface SearchViewsDrawerProps {
  open: boolean;
  onClose: () => void;
  searchId: string | null;
}

const getFeedbackColor = (feedback?: ResolutionFeedback): string => {
  switch (feedback) {
    case 'Solved':
      return '#52c41a'; // Green
    case 'NotSolved':
      return '#ff4d4f'; // Red
    case 'NoInput':
    default:
      return '#1890ff'; // Blue
  }
};

const formatStockholmTime = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};

const formatDuration = (viewedAt: string, feedbackAt?: string | null): string => {
  if (!feedbackAt) return '';

  const viewed = new Date(viewedAt);
  const feedback = new Date(feedbackAt);
  const diffMs = feedback.getTime() - viewed.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffSeconds = Math.floor((diffMs % 60000) / 1000);

  if (diffMinutes > 0) {
    return `${diffMinutes} min ${diffSeconds} sec`;
  }
  return `${diffSeconds} sec`;
};

const GuideViewItem: React.FC<{ view: GuideViewResponse }> = ({ view }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data: guideResponse, isLoading, error } = useGetApiAdminGuidesGuideId(
    view.guideId || '',
    {
      query: {
        enabled: !!view.guideId,
      },
    }
  );

  const guide = guideResponse?.guide;
  const content = guide?.content;

  if (isLoading) {
    return (
      <div style={{ padding: '12px 0' }}>
        <Spin size="small" />
        <Text style={{ marginLeft: 8 }}>{t('common.loading')}</Text>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div style={{ padding: '12px 0' }}>
        <Text type="danger">{t('common.error')}</Text>
      </div>
    );
  }

  const duration = view.resolutionFeedbackAt
    ? formatDuration(view.viewedAt || '', view.resolutionFeedbackAt)
    : null;

  const getFeedbackText = () => {
    if (view.resolutionFeedback === 'Solved') return t('searchHistory.solved');
    if (view.resolutionFeedback === 'NotSolved') return t('searchHistory.notSolved');
    return t('searchHistory.noFeedback');
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
          {content.problem} <Text type="secondary">- {getFeedbackText()}</Text>
        </Title>
        <div style={{ display: 'flex', gap: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ClockCircleOutlined /> {t('searchHistory.viewedAt')}: {formatStockholmTime(view.viewedAt || '')}
          </Text>
          {duration && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('searchHistory.duration')}: {duration}
            </Text>
          )}
        </div>
      </div>

      <Collapse
        ghost
        activeKey={expanded ? ['1'] : []}
        onChange={(keys) => setExpanded(keys.length > 0)}
      >
        <Panel
          header={expanded ? t('searchHistory.collapse') : t('searchHistory.expand')}
          key="1"
          style={{ border: 'none', padding: 0 }}
        >
          <div style={{ paddingLeft: 16, paddingTop: 8 }}>
            {content.solutions && content.solutions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>{t('searchHistory.solutions')}:</Text>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  {content.solutions.map((solution, index) => (
                    <li key={index}>
                      <Text>{solution}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {content.possibleCause && (
              <div>
                <Text strong>{t('searchHistory.possibleCause')}:</Text>
                <div style={{ marginTop: 4 }}>
                  <Text>{content.possibleCause}</Text>
                </div>
              </div>
            )}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export const SearchViewsDrawer: React.FC<SearchViewsDrawerProps> = ({
  open,
  onClose,
  searchId,
}) => {
  const { t } = useTranslation();

  const {
    data: viewsResponse,
    isLoading,
    error,
  } = useGetApiAdminSearchHistorySearchIdViews(
    searchId || '',
    { page: 1, pageSize: 50 },
    {
      query: {
        enabled: !!searchId && open,
      },
    }
  );

  // Sort views by viewedAt in chronological order (oldest first)
  const views = (viewsResponse?.views || []).sort((a, b) => {
    const dateA = new Date(a.viewedAt || '').getTime();
    const dateB = new Date(b.viewedAt || '').getTime();
    return dateA - dateB;
  });
  const hasError = !!error;

  return (
    <Drawer
      title={t('searchHistory.viewsDrawerTitle')}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>{t('common.loading')}</Text>
          </div>
        </div>
      )}

      {hasError && (
        <Alert
          message={t('common.error')}
          description={t('common.error')}
          type="error"
          showIcon
        />
      )}

      {!isLoading && !hasError && views.length === 0 && (
        <Alert
          message={t('searchHistory.noViews')}
          type="info"
          showIcon
        />
      )}

      {!isLoading && !hasError && views.length > 0 && (
        <Timeline
          items={views.map((view) => ({
            dot: (
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: getFeedbackColor(view.resolutionFeedback),
                }}
              />
            ),
            children: <GuideViewItem view={view} />,
          }))}
        />
      )}
    </Drawer>
  );
};
